/**
 * Creates event helpers for tracking analytics events.
 *
 * Why we need this: Each event type needs the same set of functions
 * (create, validate, transform) but with different data shapes.
 * This factory generates those functions automatically.
 */

import { z } from 'zod';

import { debugCohesionError } from '../../../utils/debug-logger';

// #region Deduplication Config Types

/**
 * @typedef {'page' | 'none'} DeduplicationScope
 * - 'page': One event per unique key per page view (resets on navigation)
 * - 'none': No deduplication, event fires every time (default)
 */

/**
 * @typedef {object} DeduplicationConfig
 * @property {DeduplicationScope} scope - When to deduplicate ('page' or 'none')
 * @property {(params: any) => string} [keyBuilder] - Function to build unique key from params.
 *   If omitted when scope='page', uses eventName as key (one event per page).
 *
 * @example One event per page (e.g., AccountLogin)
 * deduplication: { scope: 'page' }
 * // Key: 'AccountLogin' — only one login event fires per page view
 *
 * @example One event per unique element (e.g., ElementViewed)
 * deduplication: {
 *   scope: 'page',
 *   keyBuilder: (params) => `${params.webElement.name}:${params.webElement.location}`
 * }
 * // Key: 'ElementViewed:product-card:hero' — each unique element fires once per page
 */

// #endregion

/**
 * Builds helpers for a Cohesion ⇢ Tealium event.
 *
 * @template {z.ZodRawShape} Shape
 * @param {Object} cfg
 * @param {string} cfg.eventName - The Cohesion event name
 * @param {Shape} cfg.cohesionSchema - Data structure that matches Cohesion's schema
 * @param {'link'|'view'} [cfg.tealiumMethodType='link'] - Use 'link' for 99.99% of events (user interactions)
 * @param {DeduplicationConfig} [cfg.deduplication] - How to prevent duplicate events
 * @see TealiumProvider for details on 'link' vs 'view' methods
 *
 * @example Basic event (no deduplication)
 * defineEvent({
 *   eventName: 'core.ElementClicked.v1',
 *   cohesionSchema: { ... },
 * })
 *
 * @example Page-view event (one per page view)
 * defineEvent({
 *   eventName: 'core.AccountLogin.v1',
 *   cohesionSchema: { ... },
 *   deduplication: { scope: 'page' },
 * })
 *
 * @example Page-view scoped with unique key
 * defineEvent({
 *   eventName: 'redventures.usertracking.v3.ElementViewed',
 *   cohesionSchema: { ... },
 *   deduplication: {
 *     scope: 'page',
 *     keyBuilder: (params) => `${params.webElement.name}:${params.webElement.location}`,
 *   },
 * })
 */
export function defineEvent({
  eventName,
  cohesionSchema,
  tealiumMethodType = 'link',
  deduplication = { scope: 'none' },
}) {
  // Two different schemas because data flows through two different formats:

  // 1. React components provide the data in a simple object matching Cohesion's schema
  const ReactComponentInputSchema = z.strictObject(cohesionSchema);

  // 2. Tealium API needs extra metadata (like schemaTypes array)
  const TealiumApiOutputSchema = z.strictObject({
    ...cohesionSchema,
    schemaTypes: z.tuple([z.literal(eventName)]), // Required by Tealium
  });

  /** @typedef {z.infer<typeof ReactComponentInputSchema>} ReactComponentInputParams - The clean data object that React components provide */
  /** @typedef {z.infer<typeof TealiumApiOutputSchema>} TealiumApiOutputPayload - The formatted data object that gets sent to Tealium */

  /**
   * Transforms React component's event format into Tealium API format.
   *
   * @param {{ params: ReactComponentInputParams }} event
   * @returns {TealiumApiOutputPayload}
   */
  const convertToTealiumFormat = (event) =>
    /** @type {TealiumApiOutputPayload} */ ({
      schemaTypes: [eventName], // Tealium needs this to route the event
      ...event.params, // Spread the actual data
    });

  /**
   * Creates an event object that React components can pass to `trackEvent()`
   * @param {ReactComponentInputParams} params - The event data (e.g. { pageName: "Home" })
   * @returns {{ name: string; params: ReactComponentInputParams }} - Complete event ready for `trackEvent()`
   */
  const createEventPayload = (params) => {
    // In development: validate that React component provided correct data shape
    // In production: skip validation for performance
    if (process.env.NODE_ENV !== 'production') {
      try {
        ReactComponentInputSchema.parse(params);
      } catch (err) {
        debugCohesionError(() =>
          console.error(
            `❌ [${eventName}] Invalid params: ${err.message}\n\nParams: ${JSON.stringify(
              params,
              null,
              2,
            )}\n`,
          ),
        );
      }
    }

    return { name: eventName, params };
  };

  /**
   * Final validation before sending to Tealium
   * Ensures we're sending exactly what Tealium expects
   */
  const validateTealiumPayload = (tealiumPayload) => {
    if (process.env.NODE_ENV !== 'production') {
      return TealiumApiOutputSchema.parse(tealiumPayload);
    }

    return tealiumPayload;
  };

  return {
    // metadata
    eventName,
    tealiumMethodType,

    deduplication,

    // helpers (these are what you'll actually use)
    createEventPayload, // React components call this
    convertToTealiumFormat, // CohesionProvider uses this internally
    validateTealiumPayload, // CohesionProvider uses this internally

    // (optional) schemas for advanced use
    ReactComponentInputSchema,
    TealiumApiOutputSchema,
  };
}
